import { useState } from 'react'
import { Card, CardHeader, CardTitle, CardDescription, CardContent, Button, Select, Badge } from '@/components/ui'
import { Fingerprint, Copy, Check, RefreshCw, User, MapPin, Phone, Mail, Calendar, CreditCard, Hash } from 'lucide-react'
import { toast } from 'sonner'
import { countries, type CountryData } from './identityData'

interface Identity {
  firstName: string
  lastName: string
  gender: string
  dob: string
  age: number
  nationalId: string
  nationalIdLabel: string
  email: string
  phone: string
  street: string
  city: string
  region: string
  regionCode: string
  zip: string
  country: string
  username: string
  password: string
  motherMaiden: string
  cardNumber: string
  cardExpiry: string
  cardCvv: string
}

const emailDomains = ['gmail.com','yahoo.com','outlook.com','hotmail.com','protonmail.com','icloud.com']

const rand = <T,>(arr: T[]): T => arr[Math.floor(Math.random() * arr.length)]
const randInt = (min: number, max: number) => Math.floor(Math.random() * (max - min + 1)) + min

function generateLuhn(prefix: string, length: number): string {
  let num = prefix
  while (num.length < length - 1) num += Math.floor(Math.random() * 10)
  let sum = 0
  let even = false
  for (let i = num.length - 1; i >= 0; i--) {
    let d = parseInt(num[i])
    if (even) { d *= 2; if (d > 9) d -= 9 }
    sum += d
    even = !even
  }
  return num + ((10 - (sum % 10)) % 10)
}

function formatPattern(pattern: string): string {
  let result = ''
  for (const ch of pattern) {
    if (ch === 'X') {
      result += Math.floor(Math.random() * 10).toString()
    } else if (ch === 'A') {
      result += String.fromCharCode(65 + Math.floor(Math.random() * 26))
    } else {
      result += ch
    }
  }
  return result
}

function formatDate(day: number, month: number, year: number, format: 'MDY' | 'DMY' | 'YMD'): string {
  const d = String(day).padStart(2, '0')
  const m = String(month).padStart(2, '0')
  const y = String(year)
  switch (format) {
    case 'MDY': return `${m}/${d}/${y}`
    case 'DMY': return `${d}/${m}/${y}`
    case 'YMD': return `${y}/${m}/${d}`
  }
}

function generateIdentity(countryCode: string, gender: string): Identity {
  const data: CountryData = countries[countryCode] || countries.US
  const isMale = gender === 'random' ? Math.random() > 0.5 : gender === 'male'
  const firstName = isMale ? rand(data.maleNames) : rand(data.femaleNames)
  const lastName = rand(data.lastNames)

  const region = rand(data.regions)
  const city = rand(region[2])
  const streetNum = randInt(1, 999)
  const street = `${streetNum} ${rand(data.streets)}`
  const zip = data.zipFormat ? formatPattern(data.zipFormat) : ''

  const birthYear = randInt(1960, 2004)
  const birthMonth = randInt(1, 12)
  const birthDay = randInt(1, 28)
  const dob = formatDate(birthDay, birthMonth, birthYear, data.dateFormat)
  const age = new Date().getFullYear() - birthYear

  const nationalId = formatPattern(data.idFormat)
  const phone = formatPattern(data.phoneFormat)

  const cleanFirst = firstName.replace(/[^a-zA-Z]/g, '').toLowerCase()
  const cleanLast = lastName.replace(/[^a-zA-Z]/g, '').toLowerCase()
  const emailUser = `${cleanFirst}${cleanLast}${randInt(1, 999)}`
  const email = `${emailUser}@${rand(emailDomains)}`
  const username = `${cleanFirst}_${cleanLast}${randInt(1, 99)}`

  const chars = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%'
  const password = Array.from({ length: 12 }, () => chars[Math.floor(Math.random() * chars.length)]).join('')

  const motherMaiden = rand(data.lastNames)

  const bins = ['4147','4532','5425','5178','3714','6011']
  const bin = rand(bins)
  const cardLen = bin.startsWith('37') || bin.startsWith('34') ? 15 : 16
  const cardNumber = generateLuhn(bin, cardLen)
  const expMonth = String(randInt(1, 12)).padStart(2, '0')
  const expYear = String(new Date().getFullYear() + randInt(1, 5))
  const cardCvv = String(randInt(100, 999))

  return {
    firstName, lastName,
    gender: isMale ? 'Male' : 'Female',
    dob, age,
    nationalId,
    nationalIdLabel: data.idLabel,
    email, phone,
    street, city, region: region[0], regionCode: region[1], zip,
    country: data.name, username, password, motherMaiden,
    cardNumber, cardExpiry: `${expMonth}/${expYear}`, cardCvv,
  }
}

function FieldRow({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) {
  const [copied, setCopied] = useState(false)

  const copy = async () => {
    await navigator.clipboard.writeText(value)
    setCopied(true)
    setTimeout(() => setCopied(false), 1500)
  }

  return (
    <div className="flex items-center gap-3 py-2 border-b border-border/50 last:border-0 group">
      <div className="text-muted-foreground shrink-0">{icon}</div>
      <div className="flex-1 min-w-0">
        <p className="text-xs text-muted-foreground">{label}</p>
        <p className="text-sm font-mono truncate">{value}</p>
      </div>
      <Button
        variant="ghost"
        size="icon"
        className="h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity shrink-0"
        onClick={copy}
      >
        {copied ? <Check className="h-3 w-3 text-green-400" /> : <Copy className="h-3 w-3" />}
      </Button>
    </div>
  )
}

const sortedCountries = Object.entries(countries).sort((a, b) => a[1].name.localeCompare(b[1].name))

export function IdentityGenerator() {
  const [country, setCountry] = useState('US')
  const [gender, setGender] = useState('random')
  const [quantity, setQuantity] = useState(1)
  const [identities, setIdentities] = useState<Identity[]>([])

  const generate = () => {
    const results: Identity[] = []
    for (let i = 0; i < quantity; i++) {
      results.push(generateIdentity(country, gender))
    }
    setIdentities(results)
    toast.success(`Generated ${quantity} ${quantity === 1 ? 'identity' : 'identities'}`)
  }

  const copyAll = async (id: Identity) => {
    const text = [
      `Name: ${id.firstName} ${id.lastName}`,
      `Gender: ${id.gender}`,
      `DOB: ${id.dob} (Age: ${id.age})`,
      `${id.nationalIdLabel}: ${id.nationalId}`,
      `Email: ${id.email}`,
      `Phone: ${id.phone}`,
      `Address: ${id.street}, ${id.city}, ${id.regionCode} ${id.zip}`,
      `Country: ${id.country}`,
      `Username: ${id.username}`,
      `Password: ${id.password}`,
      `Mother's Maiden: ${id.motherMaiden}`,
      `Card: ${id.cardNumber}|${id.cardExpiry}|${id.cardCvv}`,
    ].join('\n')
    await navigator.clipboard.writeText(text)
    toast.success('Identity copied to clipboard')
  }

  const copyAllBulk = async () => {
    const text = identities.map((id, i) => [
      `--- Identity ${i + 1} ---`,
      `${id.firstName} ${id.lastName} | ${id.gender} | ${id.dob}`,
      `${id.nationalIdLabel}: ${id.nationalId} | Phone: ${id.phone}`,
      `${id.street}, ${id.city}, ${id.regionCode} ${id.zip}, ${id.country}`,
      `Email: ${id.email}`,
      `Card: ${id.cardNumber}|${id.cardExpiry}|${id.cardCvv}`,
    ].join('\n')).join('\n\n')
    await navigator.clipboard.writeText(text)
    toast.success(`Copied ${identities.length} identities`)
  }

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <div className="flex items-center gap-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-primary/20 to-purple-500/20">
            <Fingerprint className="h-6 w-6 text-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Identity Generator</h1>
            <p className="text-muted-foreground">Generate realistic fake identities for testing</p>
          </div>
        </div>
      </div>

      <Card className="glass border-yellow-500/50">
        <CardContent className="p-4">
          <p className="text-sm text-yellow-500">
            <strong>Disclaimer:</strong> Generated data is completely fictional.
            For testing and development purposes only.
          </p>
        </CardContent>
      </Card>

      {/* Controls */}
      <Card className="glass">
        <CardContent className="pt-6">
          <div className="flex flex-wrap items-end gap-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Country</label>
              <Select value={country} onChange={(e) => setCountry(e.target.value)} className="w-48">
                {sortedCountries.map(([code, data]) => (
                  <option key={code} value={code}>{data.name}</option>
                ))}
              </Select>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Gender</label>
              <Select value={gender} onChange={(e) => setGender(e.target.value)} className="w-32">
                <option value="random">Random</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
              </Select>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Quantity</label>
              <Select value={String(quantity)} onChange={(e) => setQuantity(Number(e.target.value))} className="w-24">
                {[1, 3, 5, 10, 25, 50].map(n => (
                  <option key={n} value={String(n)}>{n}</option>
                ))}
              </Select>
            </div>
            <Button onClick={generate}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Generate
            </Button>
            {identities.length > 1 && (
              <Button variant="outline" onClick={copyAllBulk}>
                <Copy className="h-4 w-4 mr-2" />
                Copy All
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Results */}
      {identities.length > 0 && (
        <div className="grid gap-6 lg:grid-cols-2">
          {identities.map((id, idx) => (
            <Card key={idx} className="glass">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <CardTitle className="text-base">{id.firstName} {id.lastName}</CardTitle>
                    <Badge variant="secondary" className="text-xs">{id.gender}</Badge>
                    <Badge variant="secondary" className="text-xs">{id.country}</Badge>
                  </div>
                  <Button variant="outline" size="sm" onClick={() => copyAll(id)}>
                    <Copy className="h-3 w-3 mr-1" />
                    Copy
                  </Button>
                </div>
                {identities.length > 1 && (
                  <CardDescription>Identity #{idx + 1}</CardDescription>
                )}
              </CardHeader>
              <CardContent className="space-y-0">
                <FieldRow icon={<User className="h-4 w-4" />} label="Full Name" value={`${id.firstName} ${id.lastName}`} />
                <FieldRow icon={<Calendar className="h-4 w-4" />} label="Date of Birth" value={`${id.dob} (Age: ${id.age})`} />
                <FieldRow icon={<Hash className="h-4 w-4" />} label={id.nationalIdLabel} value={id.nationalId} />
                <FieldRow icon={<Mail className="h-4 w-4" />} label="Email" value={id.email} />
                <FieldRow icon={<Phone className="h-4 w-4" />} label="Phone" value={id.phone} />
                <FieldRow icon={<MapPin className="h-4 w-4" />} label="Address" value={`${id.street}, ${id.city}, ${id.regionCode} ${id.zip}`} />
                <FieldRow icon={<MapPin className="h-4 w-4" />} label="Country" value={id.country} />
                <FieldRow icon={<User className="h-4 w-4" />} label="Username" value={id.username} />
                <FieldRow icon={<Hash className="h-4 w-4" />} label="Password" value={id.password} />
                <FieldRow icon={<User className="h-4 w-4" />} label="Mother's Maiden Name" value={id.motherMaiden} />
                <FieldRow icon={<CreditCard className="h-4 w-4" />} label="Card" value={`${id.cardNumber}|${id.cardExpiry}|${id.cardCvv}`} />
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {identities.length === 0 && (
        <div className="text-center py-16 text-muted-foreground">
          <Fingerprint className="h-12 w-12 mx-auto mb-4 opacity-30" />
          <p>Click Generate to create fake identities</p>
        </div>
      )}
    </div>
  )
}
